import { BaseComponent, define, prefersReducedMotion } from 'core';

export class CarouselSlider extends BaseComponent {
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('role')) this.setAttribute('role', 'region');
    this.setAttribute('aria-roledescription', 'carousel');

    this._track = this.querySelector('[data-carousel-track]');
    this._slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this._slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${index + 1} / ${this._slides.length}`);
    });

    this._prevBtn = this.querySelector('[data-carousel-prev]');
    this._nextBtn = this.querySelector('[data-carousel-next]');
    if (this._prevBtn)
      this.on(this._prevBtn, 'click', () => {
        this.scrollBySlide(-1);
        this._userNavigated();
      });
    if (this._nextBtn)
      this.on(this._nextBtn, 'click', () => {
        this.scrollBySlide(1);
        this._userNavigated();
      });

    this.on(this, 'keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.scrollBySlide(-1);
        this._userNavigated();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.scrollBySlide(1);
        this._userNavigated();
      }
    });

    this._initLazy();
    this._initMore();
    this._initClickGuard();

    this._dots = this.querySelector('[data-carousel-dots]');
    this._paginationLabel = this.getAttribute('data-pagination-label') || 'Go to page';

    if (this._track) {
      this.on(
        this._track,
        'scroll',
        () => {
          this._updateControls();
          this._updatePagination();
        },
        { passive: true }
      );
      this._updateControls();

      if (this._dots) {
        this._buildDots();

        // Kapalı bir <details> içinde (PDP "Complete your project" akordiyonu)
        // track genişliği bağlanma anında 0 olur ve sayfa sayısı yanlış çıkar.
        // Açılma bir window resize'ı DEĞİL, bu yüzden elemanı doğrudan izliyoruz;
        // ResizeObserver hem açılmayı hem kap genişliği değişimini yakalar.
        if ('ResizeObserver' in window) {
          this._resizeObserver = new ResizeObserver(() => {
            clearTimeout(this._resizeTimer);
            this._resizeTimer = setTimeout(() => this._buildDots(), 150);
          });
          this._resizeObserver.observe(this._track);
        } else {
          this.on(window, 'resize', () => {
            clearTimeout(this._resizeTimer);
            this._resizeTimer = setTimeout(() => this._buildDots(), 150);
          });
        }
      }
    }

    this._initAutoplay();
    this._initSlideAnim();
  }

  /**
   * Slayt giriş animasyonu — OPT-IN (`data-slide-anim`, yalnız hero). Kaydırma
   * durunca gelinen slayta `data-slide-active` + `data-slide-enter` yazılır;
   * CSS başlığı/açıklamayı/CTA'yı o anda oynatır (src/input.css "Hero slayt
   * girişi"). İlk slayt açılışta yalnız `data-slide-active` alır, `enter`
   * ALMAZ: başlığı sayfanın H1'i, görseli LCP — açılışta gizleyip oynatmak
   * bir an boş görünür ve ölçümü riske atar. Karusel başa dönünce o da oynar.
   * Azaltılmış harekette hiç kurulmaz; `data-slide-anim-ready` olmadan CSS'teki
   * hiçbir gizleme kuralı eşleşmez, yazılar durağan görünür.
   */
  _initSlideAnim() {
    if (!this.hasAttribute('data-slide-anim') || !this._track) return;
    if (prefersReducedMotion() || this._slides.length < 2) return;
    this._activeIndex = this._currentIndex();
    this._slides[this._activeIndex]?.setAttribute('data-slide-active', '');
    this.setAttribute('data-slide-anim-ready', '');
    this.on(
      this._track,
      'scroll',
      () => {
        // Kaydırma bitmesini beklemek yazıları geç getiriyordu (yumuşak
        // kaydırma + bekleme ~0.6 sn). Slayt yerine %88 oturduğu anda
        // başlatılıyor; yarıda bırakılan bir kaydırma için bekleme yedekte.
        const step = this._slides[0].getBoundingClientRect().width + this._gap();
        const pos = step ? this._track.scrollLeft / step : 0;
        if (Math.abs(pos - Math.round(pos)) < 0.12) this._markActive();
        clearTimeout(this._activeTimer);
        this._activeTimer = setTimeout(() => this._markActive(), 120);
      },
      { passive: true }
    );
  }

  _markActive() {
    const i = this._currentIndex();
    if (i === this._activeIndex) return;
    const prev = this._slides[this._activeIndex];
    if (prev) {
      prev.removeAttribute('data-slide-active');
      prev.removeAttribute('data-slide-enter');
    }
    const next = this._slides[i];
    next.removeAttribute('data-slide-enter');
    void next.offsetWidth;
    next.setAttribute('data-slide-active', '');
    next.setAttribute('data-slide-enter', '');
    this._activeIndex = i;
  }

  /**
   * Kaydırma tıklama DEĞİLDİR. Slaytlar ve kartlar baştan sona bağlantı
   * (hero'da slaydın tamamı tıklanır), yani parmakla kısa/yarım bir kaydırma
   * ya da kaydırma bitmeden gelen dokunuş ziyaretçiyi istemeden sayfadan
   * çıkarabiliyordu. Basış anındaki konum ve kaydırma değeri tutuluyor; parmak
   * 10px'ten fazla gittiyse ya da track o arada kaydıysa tıklama iptal.
   * Gerçek bir dokunuş/tıklama eskisi gibi geçer. Klavye (Enter) tıklaması
   * pointerdown üretmediği için hiç etkilenmez.
   */
  _initClickGuard() {
    if (!this._track) return;
    this.on(
      this._track,
      'pointerdown',
      (e) => {
        this._downX = e.clientX;
        this._downY = e.clientY;
        this._downScroll = this._track.scrollLeft;
        this._pointerDown = true;
      },
      { passive: true }
    );
    this.on(
      this._track,
      'click',
      (e) => {
        // detail 0 = klavyeyle (Enter) üretilen tıklama: işaretçi yok, dokunma.
        if (!this._pointerDown || e.detail === 0) return;
        this._pointerDown = false;
        const moved = Math.hypot(e.clientX - this._downX, e.clientY - this._downY) > 10;
        const scrolled = Math.abs(this._track.scrollLeft - this._downScroll) > 5;
        if (moved || scrolled) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    if (this._resizeObserver) this._resizeObserver.disconnect();
    this._stopAutoplay();
    clearTimeout(this._settleTimer);
    clearTimeout(this._activeTimer);
    super.disconnectedCallback();
  }

  /**
   * Otomatik geçiş — OPT-IN. Yalnızca `data-autoplay="<ms>"` verilirse çalışır,
   * yani mevcut carousel'lerin davranışı hiç değişmez (hero slider kullanıyor).
   *
   * Durur: prefers-reduced-motion açıksa hiç başlamaz; hover / klavye focus /
   * sekme arkaplana alındığında duraklar, geri dönünce devam eder. Sona
   * gelince başa sarar.
   */
  _initAutoplay() {
    const ms = parseInt(this.dataset.autoplay || '', 10);
    if (!ms || ms < 1000) return;
    if (prefersReducedMotion()) return;
    if (this._slides.length < 2) return;

    this._autoplayMs = ms;

    // Geri sayım halkası. Markup'ta duruyor ama GİZLİ: buraya kadar gelmek
    // otomatik geçişin gerçekten çalışacağı anlamına geliyor (reduced-motion,
    // tek slayt ve geçersiz süre yukarıda eleniyor). Böylece halka hiçbir zaman
    // dönmeyen bir sayaç göstermiyor.
    this._progress = this.querySelector('[data-carousel-progress]');
    if (this._progress) this._progress.hidden = false;

    // Halka ile sayaç AYNI ANDA başlamalı. CSS animasyonu öğe boyanır boyanmaz
    // dönmeye başlıyor, setInterval ise bileşen yükselene kadar (module + defer)
    // başlamıyor — arada yüzlerce ms fark oluyordu ve halka slayttan önce
    // doluyordu. CSS'te animasyon `paused` başlıyor; burada sıfırlanıp aynı
    // satırda çalıştırılıyor, iki saat tek noktadan kuruluyor.
    this._progressRestart();
    this._startAutoplay();

    // Hikaye slaytı bitince (fare üstte değilken) kalan süreyi beklemeden geç.
    this.on(this, 'story:done', (e) => {
      if (!this._autoplayTimer) return;
      const idx = this._slides.findIndex((s) => s.contains(e.target));
      if (idx !== this._currentIndex()) return;
      this._stopAutoplay();
      const next = this._advance();
      this._schedule(next);
      this._progressResume();
    });
    this.on(this, 'mouseenter', () => this._stopAutoplay());
    this.on(this, 'mouseleave', () => this._startAutoplay());
    this.on(this, 'focusin', () => this._stopAutoplay());
    this.on(this, 'focusout', () => this._startAutoplay());
    this.on(document, 'visibilitychange', () => {
      if (document.hidden) this._stopAutoplay();
      else this._startAutoplay();
    });

    // Parmakla kaydırma (ya da sayfa çubuğu) sayacı bilmiyordu: yeni slayt
    // ÖNCEKİNİN kalan süresiyle geçiyordu. Slayt süreleri artık farklı olabildiği
    // için (hikaye slaytı) bu, hikayeyi yarıda kesiyordu. Kaydırma durunca
    // gelinen slayt sayacın kurulduğu slayt değilse sayaç onun süresiyle
    // baştan kuruluyor. Otomatik geçişin kendi kaydırması hedefi zaten
    // `_scheduledIndex`e yazdığı için burada yeniden kurulmuyor.
    this.on(
      this._track,
      'scroll',
      () => {
        clearTimeout(this._settleTimer);
        this._settleTimer = setTimeout(() => {
          if (!this._autoplayTimer) return;
          const current = this._currentIndex();
          if (current === this._scheduledIndex) return;
          this._stopAutoplay();
          this._startAutoplay();
        }, 200);
      },
      { passive: true }
    );
  }

  /**
   * Halka duraklarken de doğruyu söylemeli: fareyi üstüne getirince sayaç
   * duruyorsa halka da durmalı, yoksa "birazdan geçecek" der ve geçmez.
   * `animation-play-state` bunu tek satırda veriyor; ilerlemeyi JS'te takip
   * etmeye gerek yok.
   */
  /**
   * Durum HOST'ta duruyor, halkanın kendisinde değil: mobilde geri sayımı
   * halka değil sayfa çubuğunun aktif dilimi gösteriyor ve o dilim CSS'te bir
   * ::after — JS'ten ona tutunmak gerekmiyor. Tek nitelik iki göstergeyi de
   * besliyor.
   */
  _progressResume() {
    this.setAttribute('data-running', '');
  }

  _progressPause() {
    this.removeAttribute('data-running');
  }

  /**
   * Kullanıcı elle geçtiğinde sayaç da baştan saymalı: yoksa oka basıp yarım
   * saniye sonra bir kez daha otomatik geçiyor ve halka o geçişi hiç
   * anlatmamış oluyor. Otomatik geçiş kapalıysa hiçbir şey yapmaz.
   */
  _userNavigated() {
    if (!this._autoplayTimer) return;
    this._stopAutoplay();
    this._progressRestart();
    this._startAutoplay();
  }

  /**
   * Slayt başına süre. Varsayılan bölüm ayarı; bir slayt `data-duration="<ms>"`
   * taşıyorsa (hero hikaye slaytı, 5 kare x 3 sn) o slaytta onun süresi geçerli.
   * Halka ve mobil çubuk da süreyi `--cp-ms`ten okuyor, yani host'a yazılan
   * değer iki göstergeyi de o slayta göre ayarlıyor.
   */
  _durationFor(index) {
    const own = parseInt(this._slides[index]?.dataset.duration || '', 10);
    return own >= 1000 ? own : this._autoplayMs;
  }

  _currentIndex() {
    if (!this._track || this._slides.length === 0) return 0;
    const step = this._slides[0].getBoundingClientRect().width + this._gap();
    if (!step) return 0;
    const i = Math.round(this._track.scrollLeft / step);
    return Math.max(0, Math.min(i, this._slides.length - 1));
  }

  /** Verilen slayt için sayacı kurar; süre dolunca ilerleyip sıradakini kurar. */
  _schedule(index) {
    const ms = this._durationFor(index);
    this._scheduledIndex = index;
    this.style.setProperty('--cp-ms', `${ms}ms`);
    // Halka yeni süreyle baştan: süre slayta göre değiştiği için eski turun
    // üstüne yeni değer yazılırsa halka yarıda hızlanıp yavaşlıyor.
    this._progressRestart();
    this._autoplayTimer = setTimeout(() => {
      this._autoplayTimer = null;
      const next = this._advance();
      this._schedule(next);
    }, ms);
  }

  /**
   * Sayaç sıfırlanınca halka da baştan başlamalı. CSS animasyonunu yeniden
   * tetiklemenin tek yolu onu kaldırıp geri koymak; arada bir reflow okunmazsa
   * tarayıcı iki değişikliği birleştirip hiçbir şey olmamış gibi davranıyor.
   */
  _progressRestart() {
    if (!this._progress) return;
    this._progress.style.animation = 'none';
    void this._progress.offsetWidth;
    this._progress.style.animation = '';
  }

  _startAutoplay() {
    if (!this._autoplayMs || this._autoplayTimer) return;
    this._schedule(this._currentIndex());
    this._progressResume();
  }

  _stopAutoplay() {
    if (!this._autoplayTimer) return;
    clearTimeout(this._autoplayTimer);
    this._autoplayTimer = null;
    this._progressPause();
  }

  /** Bir sonraki slayta geçer ve HEDEF slaytın sırasını döndürür (kaydırma
   *  henüz bitmediği için scrollLeft'ten okunamaz). */
  _advance() {
    if (!this._track) return 0;
    this._progressRestart();
    const maxScroll = this._track.scrollWidth - this._track.clientWidth;
    // 2px tolerans: subpixel yuvarlama yüzünden son slide'da eşitlik tutmayabiliyor.
    if (this._track.scrollLeft >= maxScroll - 2) {
      this._track.scrollTo({ left: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      return 0;
    }
    const next = Math.min(this._currentIndex() + 1, this._slides.length - 1);
    this.scrollBySlide(1);
    return next;
  }

  /**
   * Opsiyonel "show all" kırpması (CSS ile yapılır, bkz. collection.liquid).
   * JS'in tek işi: hiçbir şey kırpılmıyorsa (tüm kutucuklar zaten görünüyorsa)
   * butonu gizlemek. JS yoksa buton görünür kalır ve yine çalışır.
   */
  _initMore() {
    this._more = this.querySelector('[data-carousel-more]');
    this._moreToggle = this.querySelector('[data-carousel-more-toggle]');
    if (!this._more || !this._track) return;

    const update = () => {
      const expanded = this._moreToggle ? this._moreToggle.checked : false;
      const clipped = expanded || this._track.scrollHeight > this._track.clientHeight + 1;
      this._more.style.display = clipped ? '' : 'none';
    };

    update();
    if (this._moreToggle) this.on(this._moreToggle, 'change', update);
    this.on(window, 'resize', () => {
      clearTimeout(this._moreTimer);
      this._moreTimer = setTimeout(update, 150);
    });
  }

  _initLazy() {
    const lazyImages = Array.from(this.querySelectorAll('img[data-src]'));
    if (lazyImages.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      lazyImages.forEach((img) => this._loadImage(img));
      return;
    }

    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._loadImage(entry.target);
            this._observer.unobserve(entry.target);
          }
        });
      },
      { root: this._track, rootMargin: '200px' }
    );
    lazyImages.forEach((img) => this._observer.observe(img));
  }

  _loadImage(img) {
    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
      delete img.dataset.srcset;
    }
    if (img.dataset.src) {
      img.src = img.dataset.src;
      delete img.dataset.src;
    }
  }

  scrollBySlide(direction) {
    if (!this._track || this._slides.length === 0) return;
    const step = this._slides[0].getBoundingClientRect().width + this._gap();
    this._track.scrollBy({
      left: direction * step,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  _gap() {
    if (!this._track) return 0;
    const style = getComputedStyle(this._track);
    return parseFloat(style.columnGap || style.gap || '0') || 0;
  }

  _updateControls() {
    if (!this._track) return;
    const maxScroll = this._track.scrollWidth - this._track.clientWidth - 1;

    const hasOverflow = this._track.scrollWidth > this._track.clientWidth + 1;
    if (this._prevBtn) {
      this._prevBtn.style.display = hasOverflow ? '' : 'none';
      this._prevBtn.disabled = this._track.scrollLeft <= 0;
    }
    if (this._nextBtn) {
      this._nextBtn.style.display = hasOverflow ? '' : 'none';
      this._nextBtn.disabled = this._track.scrollLeft >= maxScroll;
    }
  }

  _pageCount() {
    const pageWidth = this._track.clientWidth;
    if (pageWidth === 0) return 1;
    return Math.max(1, Math.ceil((this._track.scrollWidth - 1) / pageWidth));
  }

  _buildDots() {
    if (!this._track || !this._dots) return;
    const pages = this._pageCount();

    if (this._dots.children.length !== pages) {
      this._dots.textContent = '';
      for (let i = 0; i < pages; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        // Tek parça ilerleme çubuğu: her sayfa flex-1 bir dilim (dokunma alanı
        // 24px yüksek, WCAG 2.5.8). Renk/yükseklik/boşluk src/input.css'te —
        // burada utility basmıyoruz ki tema o kuralları ezmek zorunda kalmasın.
        dot.className = 'group flex min-h-6 flex-1 cursor-pointer items-center';
        dot.setAttribute('aria-label', `${this._paginationLabel} ${i + 1}`);
        const dotVisual = document.createElement('span');
        dotVisual.className = 'w-full transition-colors';
        dot.appendChild(dotVisual);
        dot.addEventListener('click', () => {

          const left = i === pages - 1 ? this._track.scrollWidth : i * this._track.clientWidth;
          this._track.scrollTo({
            left,
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          });
        });
        this._dots.appendChild(dot);
      }
    }

    // Tek sayfada çubuk yok. Masaüstünde ise HİÇ gösterilmiyor — bunu CSS
    // hallediyor (src/input.css, md ve üstü). Burada inline stil yalnızca
    // "sayfa sayısı yetersiz" durumunu kapatıyor; boş bırakıldığında medya
    // sorgusu devreye giriyor, yani iki kural birbirini ezmeden birleşiyor.
    this._dots.style.display = pages <= 1 ? 'none' : '';
    this._updatePagination();
  }

  _updatePagination() {
    if (!this._track || !this._dots || this._dots.children.length === 0) return;
    const pageWidth = this._track.clientWidth || 1;
    const lastIndex = this._dots.children.length - 1;
    const maxScroll = this._track.scrollWidth - this._track.clientWidth;

    let active;
    if (maxScroll > 0 && this._track.scrollLeft >= maxScroll - 1) {
      active = lastIndex;
    } else {
      active = Math.round(this._track.scrollLeft / pageWidth);
    }
    active = Math.max(0, Math.min(active, lastIndex));
    // Dolgu KÜMÜLATİF: 3 sayfadan 2.'deysek 1 ve 2 dolu. Çubuk "kaçıncı
    // sayfadayım" değil "listenin neresindeyim" sorusunu cevaplıyor.
    // aria-current yalnızca gerçek aktif dilimde kalıyor (ekran okuyucu için
    // tek bir "current" olmalı); dolgu ayrı bir data niteliğiyle işaretleniyor.
    Array.from(this._dots.children).forEach((dot, index) => {
      if (index === active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
      dot.toggleAttribute('data-filled', index <= active);
    });
  }
}

define('carousel-slider', CarouselSlider);
